import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Building2, ShoppingCart, ClipboardCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const money = (cur, n) => `${cur || "GTQ"} ${Number(n || 0).toLocaleString("es-GT", { minimumFractionDigits: 2 })}`;

export default function Approvals() {
  const { can } = useAuth();
  const [data, setData] = useState({ clients: [], purchase_orders: [] });
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/approvals").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const approveClient = async (c, decision) => { try { await api.post(`/clients/${c.id}/approve`, { decision }); toast.success(decision === "approve" ? "Cliente aprobado" : "Cliente rechazado"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const authorizePO = async (p, decision) => { try { await api.post(`/purchases/${p.id}/authorize`, { decision }); toast.success(decision === "approve" ? "Orden autorizada" : "Orden rechazada"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const total = data.clients.length + data.purchase_orders.length;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-primary/10 flex items-center justify-center"><ClipboardCheck className="h-6 w-6 text-primary" /></div>
        <div>
          <h2 className="text-lg font-heading font-bold">Aprobaciones</h2>
          <p className="text-sm text-muted-foreground">{total === 0 ? "No hay pendientes 🎉" : `${total} pendiente(s) de tu autorización`}</p>
        </div>
      </div>

      {can("clients:approve") && (
        <section className="space-y-3">
          <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">Clientes por aprobar <Badge className="ml-1 rounded-sm bg-amber-100 text-amber-700">{data.clients.length}</Badge></h3></div>
          {data.clients.length === 0 ? <p className="text-sm text-muted-foreground pl-6">Sin clientes pendientes.</p> : (
            <div className="grid gap-3 sm:grid-cols-2">
              {data.clients.map((c) => (
                <Card key={c.id} className="rounded-sm p-4 border shadow-none" data-testid={`approval-client-${c.code}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold">{c.business_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{c.code} · {c.nit || "sin NIT"}</p>
                      <p className="text-xs mt-1">Vendedor: <span className="font-medium">{c.vendor_name}</span></p>
                      {c.credit_days > 0 && <p className="text-xs text-muted-foreground">Crédito: {c.credit_days} días · Límite {c.currency} {c.credit_limit}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="rounded-sm gap-1 flex-1 bg-teal-600 hover:bg-teal-700" onClick={() => approveClient(c, "approve")} data-testid={`approve-client-btn-${c.code}`}><CheckCircle2 className="h-3.5 w-3.5" /> Aprobar</Button>
                    <Button size="sm" variant="outline" className="rounded-sm gap-1 text-destructive" onClick={() => approveClient(c, "reject")} data-testid={`reject-client-btn-${c.code}`}><XCircle className="h-3.5 w-3.5" /> Rechazar</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      )}

      {can("purchases:approve") && (
        <section className="space-y-3">
          <div className="flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">Órdenes de compra por autorizar <Badge className="ml-1 rounded-sm bg-purple-100 text-purple-700">{data.purchase_orders.length}</Badge></h3></div>
          {data.purchase_orders.length === 0 ? <p className="text-sm text-muted-foreground pl-6">Sin órdenes pendientes.</p> : (
            <div className="grid gap-3 sm:grid-cols-2">
              {data.purchase_orders.map((p) => (
                <Card key={p.id} className="rounded-sm p-4 border shadow-none" data-testid={`approval-po-${p.number}`}>
                  <div>
                    <p className="font-semibold">{p.number} · {p.supplier_name}</p>
                    <p className="text-xs text-muted-foreground">Creada por {p.vendor_name || "—"} · {p.date}</p>
                    <p className="text-sm mt-1 font-mono">{money(p.currency, p.subtotal)}</p>
                    <p className="text-xs text-muted-foreground">{(p.items || []).length} línea(s)</p>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="rounded-sm gap-1 flex-1 bg-teal-600 hover:bg-teal-700" onClick={() => authorizePO(p, "approve")} data-testid={`authorize-po-btn-${p.number}`}><CheckCircle2 className="h-3.5 w-3.5" /> Autorizar</Button>
                    <Button size="sm" variant="outline" className="rounded-sm gap-1 text-destructive" onClick={() => authorizePO(p, "reject")} data-testid={`reject-po-btn-${p.number}`}><XCircle className="h-3.5 w-3.5" /> Rechazar</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
